import React, { useState } from 'react';
import {
  Box, Button, Flex, HStack, IconButton, Text, VStack,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import formatMoney from '../../utils/money';

const UNLIMITED = -1;
const LOW_STOCK_THRESHOLD = 10;
const MAX_PER_ADD = 20;

export interface TicketPriceBand {
  /** EventPrice.id is a GraphQL ID (string); EventTier.id is a Float. */
  id?: string | number | null;
  name: string;
  price: number;
  order?: number | null;
  active?: boolean | null;
}

export interface TicketTier {
  id?: string | number | null;
  name: string;
  price: number;
  availableSpaces?: number | null;
  maxSpaces?: number | null;
  showSeats?: boolean | null;
  prices?: TicketPriceBand[] | null;
}

/** One basket add: a price band (or, for a legacy event, its tier) and how many. */
export interface TicketLine {
  itemId: number;
  itemType: 'EventPrice' | 'EventTier';
  quantity: number;
}

interface Props {
  /**
   * Absent when the event was loaded without its tiers relation, empty when it
   * genuinely has none. Both render the same message, but only one of them used
   * to be representable — and mapping over the absent case threw.
   */
  tiers?: TicketTier[] | null;
  /**
   * Adds every selected line in one go. Resolve false to keep the selection
   * (the add failed and the customer should be able to try again).
   */
  onAdd: (lines: TicketLine[]) => Promise<boolean | void> | boolean | void;
  isAdding?: boolean;
}

/** A tier with -1 available has no ceiling, so it is never sold out. */
export const isTierSoldOut = (tier: TicketTier): boolean => tier.availableSpaces === 0;

/**
 * How many seats a tier can still sell, capped for the stepper.
 * Unlimited tiers report -1 and are capped at MAX_PER_ADD.
 */
export const maxSelectable = (tier: TicketTier): number => {
  if (tier.availableSpaces === UNLIMITED || tier.availableSpaces === null
    || tier.availableSpaces === undefined) return MAX_PER_ADD;
  return Math.min(tier.availableSpaces, MAX_PER_ADD);
};

/**
 * Price bands to show for a tier.
 *
 * Falls back to a single synthetic band from the tier's own price for events
 * created before bands existed, so a legacy event still sells.
 */
export const bandsFor = (tier: TicketTier): TicketPriceBand[] => {
  const active = (tier.prices || []).filter((band) => band.active !== false);
  if (active.length) {
    return [...active].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }
  return [{ id: null, name: tier.name, price: tier.price }];
};

/** The cheapest ticket still on sale, for the "From £x" line. Null when none are. */
export const lowestPrice = (tiers?: TicketTier[] | null): number | null => {
  const prices = (tiers ?? [])
    .filter((tier) => !isTierSoldOut(tier))
    .flatMap((tier) => bandsFor(tier).map((band) => band.price));
  return prices.length ? Math.min(...prices) : null;
};

const plural = (n: number) => `${n} ${n === 1 ? 'ticket' : 'tickets'}`;

/**
 * Ticket selection for an event.
 *
 * Capacity belongs to the tier (the seat category) and price to the band, so
 * bands are nested under their tier and the remaining-seat count is shown once
 * per tier — every band draws from that same pool. That is also why a tier's
 * steppers are capped together: two Adults and a Child take three seats.
 *
 * The customer picks every band they want, sees one total, and adds it all
 * with one button. A button per band made three identical calls to action
 * compete, and started each counter at 1 so the page looked pre-filled.
 */
const TicketTierList = ({ tiers, onAdd, isAdding = false }: Props) => {
  const tierList = tiers ?? [];
  // Keyed by tier id + band id so two bands never share a counter.
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const keyFor = (tier: TicketTier, band: TicketPriceBand) => `${tier.id}-${band.id ?? 'flat'}`;
  const qtyFor = (tier: TicketTier, band: TicketPriceBand) => quantities[keyFor(tier, band)] ?? 0;
  const tierSelected = (tier: TicketTier) => bandsFor(tier)
    .reduce((sum, band) => sum + qtyFor(tier, band), 0);

  const setQty = (tier: TicketTier, band: TicketPriceBand, next: number) => {
    const others = tierSelected(tier) - qtyFor(tier, band);
    const clamped = Math.max(0, Math.min(next, maxSelectable(tier) - others));
    setQuantities((prev) => ({ ...prev, [keyFor(tier, band)]: clamped }));
  };

  if (!tierList.length) {
    // Previously an empty list rendered an empty card, which reads as a page
    // that failed to load rather than an event with nothing on sale yet.
    return <Text>Tickets for this event are not on sale yet.</Text>;
  }

  const lines: TicketLine[] = [];
  let total = 0;
  tierList.forEach((tier) => bandsFor(tier).forEach((band) => {
    const quantity = qtyFor(tier, band);
    if (!quantity) return;
    // A band with no id predates price bands and is added via its tier.
    lines.push({
      itemId: Number(band.id ?? tier.id),
      itemType: band.id ? 'EventPrice' : 'EventTier',
      quantity,
    });
    total += quantity * band.price;
  }));
  const count = lines.reduce((sum, line) => sum + line.quantity, 0);
  const allSoldOut = tierList.every(isTierSoldOut);

  const addAll = async () => {
    // Clear the selection only on a confirmed add. If onAdd throws, the
    // customer keeps what they chose rather than losing it to an error.
    let kept: boolean | void = false;
    try {
      kept = await onAdd(lines);
    } catch (error) {
      console.error('Adding tickets failed:', error);
      kept = false;
    }
    if (kept !== false) setQuantities({});
  };

  let ctaLabel = 'Select tickets';
  if (allSoldOut) ctaLabel = 'Sold out';
  else if (count) ctaLabel = `Add ${plural(count)} to basket`;

  return (
    <VStack align="stretch" spacing={5}>
      {tierList.map((tier) => {
        const soldOut = isTierSoldOut(tier);
        const remaining = tier.availableSpaces;
        const showCount = tier.showSeats
          && typeof remaining === 'number'
          && remaining > 0
          && remaining <= LOW_STOCK_THRESHOLD;
        const full = tierSelected(tier) >= maxSelectable(tier);

        let status: { label: string; color: string } | null = null;
        if (soldOut) status = { label: 'Sold out', color: 'red.600' };
        else if (showCount) {
          status = { label: remaining === 1 ? 'Last seat' : `Only ${remaining} left`, color: 'orange.600' };
        }

        return (
          <Box key={tier.id ?? tier.name}>
            <Flex justify="space-between" align="center" pb={2.5} borderBottom="1px solid" borderColor="#eeeef4">
              <Text fontSize="13px" fontWeight="bold" letterSpacing="0.06em" textTransform="uppercase" color="gray.500">
                {tier.name}
              </Text>
              {status && (
                <HStack spacing={1.5} fontSize="13px" color={status.color}>
                  <Box w="6px" h="6px" borderRadius="full" bg="currentColor" />
                  <Text>{status.label}</Text>
                </HStack>
              )}
            </Flex>

            {bandsFor(tier).map((band) => {
              const qty = qtyFor(tier, band);

              return (
                <Flex
                  key={band.id ?? `${tier.id}-flat`}
                  align="center"
                  justify="space-between"
                  gap={3}
                  minH="60px"
                  py={2}
                  borderBottom="1px solid"
                  borderColor="#f1f1f6"
                >
                  <Box>
                    <Text fontSize="16px" color="gray.700">{band.name}</Text>
                    <Text fontSize="15px" fontWeight="bold" color="blue.900">{formatMoney(band.price)}</Text>
                  </Box>

                  <HStack spacing={3}>
                    <IconButton
                      aria-label={`Fewer ${band.name} tickets`}
                      icon={<MinusIcon boxSize="10px" />}
                      isRound
                      variant="outline"
                      w="36px"
                      h="36px"
                      minW="36px"
                      borderColor="#d6d6e4"
                      color="blue.900"
                      isDisabled={soldOut || qty <= 0}
                      onClick={() => setQty(tier, band, qty - 1)}
                    />
                    <Text minW="20px" textAlign="center" fontWeight="bold" color="gray.900" aria-live="polite">
                      {qty}
                    </Text>
                    <IconButton
                      aria-label={`More ${band.name} tickets`}
                      icon={<AddIcon boxSize="10px" />}
                      isRound
                      variant="outline"
                      w="36px"
                      h="36px"
                      minW="36px"
                      borderColor="#d6d6e4"
                      color="blue.900"
                      isDisabled={soldOut || full}
                      onClick={() => setQty(tier, band, qty + 1)}
                    />
                  </HStack>
                </Flex>
              );
            })}
          </Box>
        );
      })}

      <Flex justify="space-between" align="baseline">
        <Text fontSize="15px" color="gray.600">
          {count ? plural(count) : 'No tickets selected'}
        </Text>
        <Text fontSize="20px" fontWeight="bold" color="blue.900">{formatMoney(total)}</Text>
      </Flex>

      <Button
        h="52px"
        w="100%"
        borderRadius="10px"
        border="none"
        bg="yellow.900"
        bgGradient="none"
        color="#1d1c4d"
        fontWeight="bold"
        fontSize="16px"
        letterSpacing="0.02em"
        _hover={{ bgGradient: 'none', filter: 'brightness(0.96)' }}
        _active={{ bgGradient: 'none', filter: 'brightness(0.92)' }}
        isDisabled={!count || allSoldOut}
        isLoading={isAdding}
        loadingText="Adding…"
        onClick={addAll}
      >
        {ctaLabel}
      </Button>

      <Text fontSize="13px" lineHeight="1.5" color="gray.500" textAlign="center" mt={-1}>
        {allSoldOut
          ? 'Every seat for this event has been booked.'
          : 'Seats are held for you once they’re in your basket.'}
      </Text>
    </VStack>
  );
};

export default TicketTierList;
